/**
 * Which in-app virtual folder (mail_custom_folders) does an email belong to?
 *
 * Two routing rules, in this order of authority:
 *   1. Outlook folder NAME - for mail the owner filed into an Outlook folder
 *      (emails.folder 'archive' | 'filed'). The owner's virtual folders mirror
 *      their Outlook folders ("Trinity", "Tenders/AusTender", ...), so the
 *      folder the mail physically sits in is the strongest signal we have.
 *   2. Sender rule - any folder whose senderEmails lists the sender. This is
 *      the only rule for inbox/junk mail and the fallback for filed mail.
 *
 * Pure (no React, no Firestore) so the feed, and anything else that needs to
 * count or route mail, can share one implementation.
 */
import { normalizeEmailFolder, isOutlookFiled } from './emailFolder';

export interface VirtualFolderLike {
  id: string;
  name: string;
  senderEmails?: string[];
  /** Group headers hold child folders, never mail. */
  isGroup?: boolean;
  deleted?: boolean;
}

export interface VirtualFolderMatchable {
  folder?: unknown;
  deleted?: unknown;
  emailType?: unknown;
  quarantined?: unknown;
  outlookFolderName?: unknown;
  outlookFolderPath?: unknown;
  virtualFolderId?: string;
  from?: ReadonlyArray<{ address?: string | null }> | null;
  senderEmail?: string | null;
}

/** Case/space-insensitive comparison key for one name or path segment. */
const clean = (s: unknown): string =>
  typeof s === 'string' ? s.trim().replace(/\s+/g, ' ').toLowerCase() : '';

/**
 * "Personal/Trinity" -> ['personal', 'trinity']. Accepts / and \ as separators
 * and ignores blanks, so stray spaces around a segment never defeat a match.
 */
export function folderNameSegments(name: unknown): string[] {
  if (typeof name !== 'string') return [];
  return name.split(/[\\/]+/).map(clean).filter(Boolean);
}

/** Does `needle` appear in `hay` as one contiguous run of whole segments? */
function containsRun(hay: string[], needle: string[]): boolean {
  if (needle.length === 0 || needle.length > hay.length) return false;
  for (let i = 0; i + needle.length <= hay.length; i++) {
    if (needle.every((seg, j) => hay[i + j] === seg)) return true;
  }
  return false;
}

// Match strength. Only ordering matters: when several virtual folders could
// claim the same mail, the most specific one wins (a "Trinity" folder beats a
// "Personal" folder for mail filed under Personal/Trinity).
const SCORE_FULL_PATH = 100;   // virtual name == the whole Outlook path
const SCORE_LEAF = 90;         // virtual name == the immediate Outlook folder
const SCORE_LEAF_OF_PATH = 85; // virtual "Tenders/AusTender" vs Outlook "AusTender"
const SCORE_ANCESTOR_RUN = 60; // virtual name is a run of segments inside the path
const SCORE_ANCESTOR = 50;     // virtual leaf equals some other path segment

/**
 * The virtual folder whose NAME mirrors the Outlook folder this email sits in,
 * or undefined. The immediate folder name and every segment of the folder path
 * are compared, exact segment equality only: "AusTender" and "Tenders/AusTender"
 * are the same folder, but "Personal/Trinity" and "Personal/Banking" share only
 * a parent and never match each other.
 */
export function matchVirtualFolderByOutlookName(
  email: Pick<VirtualFolderMatchable, 'outlookFolderName' | 'outlookFolderPath'>,
  folders: ReadonlyArray<VirtualFolderLike>,
): string | undefined {
  const path = folderNameSegments(email.outlookFolderPath);
  const leaf = clean(email.outlookFolderName) || path[path.length - 1] || '';
  if (!leaf) return undefined;
  // The stored name is authoritative for the leaf: if a path is present but
  // disagrees, keep both as candidates rather than trusting either blindly.
  const segs = path.length ? path.slice() : [];
  if (segs[segs.length - 1] !== leaf) segs.push(leaf);
  const fullPath = segs.join('/');

  let bestId: string | undefined;
  let best = 0;
  for (const f of folders) {
    if (!f || f.isGroup || f.deleted) continue;
    const v = folderNameSegments(f.name);
    if (v.length === 0) continue;
    const vLeaf = v[v.length - 1];
    let score = 0;
    if (v.join('/') === fullPath) score = SCORE_FULL_PATH;
    else if (vLeaf === leaf) score = v.length === 1 ? SCORE_LEAF : SCORE_LEAF_OF_PATH;
    else if (containsRun(segs, v)) score = SCORE_ANCESTOR_RUN;
    else if (segs.includes(vLeaf)) score = SCORE_ANCESTOR;
    // Strictly greater: on a tie the first folder (list order) keeps the mail,
    // so the result is stable across renders.
    if (score > best) { best = score; bestId = f.id; }
  }
  return bestId;
}

/** First folder whose sender rules list this address (case-insensitive). */
export function matchVirtualFolderBySender(
  senderAddr: string | null | undefined,
  folders: ReadonlyArray<VirtualFolderLike>,
): string | undefined {
  const addr = (senderAddr || '').trim().toLowerCase();
  if (!addr) return undefined;
  const match = folders.find((f) =>
    Array.isArray(f.senderEmails) && f.senderEmails.some((e) => String(e).toLowerCase() === addr));
  return match ? match.id : undefined;
}

/**
 * Live virtualFolderId for an email. Inbox/junk mail is re-resolved by sender
 * rule (so a newly added rule applies without a reload); filed mail is resolved
 * by Outlook folder name first, then sender rule. Sent/drafts/deleted mail keeps
 * whatever the email already carried.
 */
export function resolveVirtualFolderId(
  email: VirtualFolderMatchable,
  folders: ReadonlyArray<VirtualFolderLike>,
): string | undefined {
  const physical = normalizeEmailFolder(email);
  const filed = isOutlookFiled(physical);
  if (!filed && physical !== 'inbox' && physical !== 'junk') return email.virtualFolderId;

  if (filed) {
    const byName = matchVirtualFolderByOutlookName(email, folders);
    if (byName) return byName;
  }
  const senderAddr = email.from?.[0]?.address || email.senderEmail;
  // No sender to test: leave the email's own value, exactly as before.
  if (!senderAddr) return email.virtualFolderId;
  return matchVirtualFolderBySender(senderAddr, folders);
}
